import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import Switch from "react-bootstrap-switch";
import {Col, ControlLabel, FormControl, FormGroup, Grid, Row} from "react-bootstrap";
import Card from "components/Card/Card.jsx";
import ProductSubstanceTags from "app-components/Products/ProductSubstanceTags.jsx";
import profile from '../../../json/profile.json';
import AsyncSelect from 'react-select/lib/Async';
import axios from 'axios';

const API_BASE_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';

class ProductBaseForm extends React.Component {
    option = [
        {"statut": "médicament commercialisé"},
        {"statut": "médicament en arrêt de fabrication"},
        {"statut": "médicament en rupture d'approvisionnement"},
        {"statut": "médicament non commercialisé"},
        {"statut": "médicament retiré du marché"}
    ]

    constructor(props) {
        super(props);
        this.state = {
            productCache: new Map(),
            loadingProducts: false,
            loadError: null,
            selectedMedicaments: new Set()
        };
    }

    componentDidMount() {
        // Initialiser les médicaments sélectionnés à partir des props
        const selectedMedicaments = new Set();

        if (this.props.state.nomCommercialContreIndique) {
            const selected = Array.isArray(this.props.state.nomCommercialContreIndique)
                ? this.props.state.nomCommercialContreIndique
                : this.props.state.nomCommercialContreIndique.split('$');

            selected.forEach(item => {
                if (item) {
                    if (typeof item === 'object') {
                        selectedMedicaments.add(item.value || item.label || item);
                    } else {
                        selectedMedicaments.add(item);
                    }
                }
            });
        }

        this.setState({ selectedMedicaments });
    }

    loadProductOptions = async (input) => {
        if (!input || input.length < 2) {
            return [];
        }

        // Nettoyer et normaliser l'input
        const searchTerm = input.trim().toLowerCase();

        // Vérifier le cache d'abord
        const cachedResults = Array.from(this.state.productCache.values())
            .filter(product => {
                const matchesSearch = product.label.toLowerCase().includes(searchTerm);
                const notSelected = !this.state.selectedMedicaments.has(product.value);
                return matchesSearch && notSelected;
            })
            .slice(0, 20);

        // Si nous avons assez de résultats dans le cache, les retourner
        if (cachedResults.length >= 10) {
            return cachedResults;
        }

        this.setState({ loadingProducts: true, loadError: null });

        try {
            const response = await axios.get(`${API_BASE_URL}/products`, {
                params: {
                    nom: searchTerm,
                    limit: 50
                },
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                timeout: 10000
            });

            if (response.data && response.data.products && Array.isArray(response.data.products)) {
                // Extraire les médicaments déjà sélectionnés
                const selectedValues = Array.from(this.state.selectedMedicaments);

                const options = response.data.products
                    .filter(product => {

                        if (!product || !product.nom || typeof product.nom !== 'string') {
                            return false;
                        }

                        const isSelected = selectedValues.some(selected =>
                            selected && selected.toString().toLowerCase() === product.nom.toLowerCase()
                        );

                        return !isSelected;
                    })
                    .map(product => ({
                        label: product.nom,
                        value: product.nom,
                        id: product._id || product.id,
                        forme: product.forme || '',
                        dosage: product.dosage || '',
                        fabricant: product.fabricant || ''
                    }));

                // Mettre à jour le cache avec les nouveaux résultats
                const updatedCache = new Map(this.state.productCache);
                options.forEach(option => {
                    if (option.id && !updatedCache.has(option.id)) {
                        updatedCache.set(option.id, option);
                    }
                });

                this.setState({
                    loadingProducts: false,
                    productCache: updatedCache
                });

                return Array.from(updatedCache.values())
                    .filter(product =>
                        product.label.toLowerCase().includes(searchTerm) &&
                        !this.state.selectedMedicaments.has(product.value)
                    )
                    .slice(0, 20);
            }

            this.setState({
                loadingProducts: false,
                loadError: 'Format de réponse incorrect du serveur'
            });
            return [];

        } catch (error) {

            let errorMessage = 'Erreur de connexion au serveur';
            if (error.response) {
                if (error.response.status === 404) {
                    errorMessage = 'Service de recherche non disponible';
                } else if (error.response.status >= 500) {
                    errorMessage = 'Erreur serveur - Veuillez réessayer';
                } else {
                    errorMessage = error.response.data?.message || 'Erreur lors de la recherche';
                }
            } else if (error.code === 'ECONNABORTED') {
                errorMessage = 'La recherche a pris trop de temps';
            } else if (error.message.includes('Network Error')) {
                errorMessage = 'Problème de connexion réseau';
            }

            this.setState({
                loadingProducts: false,
                loadError: errorMessage
            });
            return [];
        }
    };

    handleProductChange = (selectedOptions) => {
        const options = selectedOptions ? (Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions]) : [];

        // Créer un Set pour suivre les médicaments sélectionnés
        const newSelectedMedicaments = new Set();
        const uniqueOptions = [];
        const uniqueMap = new Map();
        options.forEach(item => {
            if (item && item.value) {
                if (!uniqueMap.has(item.value)) {
                    uniqueMap.set(item.value, item);
                    newSelectedMedicaments.add(item.value);
                }
            }
        });

        uniqueOptions.push(...Array.from(uniqueMap.values()));
        this.setState({ selectedMedicaments: newSelectedMedicaments });
        this.props.setValue(uniqueOptions, 'nomCommercialContreIndique');
    };

    clearCache = () => {
        this.setState({ productCache: new Map() });
    };

    render() {
        const { state, setValue } = this.props;
        const { loadingProducts, loadError } = this.state;
        const nomCommercialValue = state.nomCommercialContreIndique
            ? (Array.isArray(state.nomCommercialContreIndique)
                ? state.nomCommercialContreIndique
                : [])
            : [];

        return (
            <Card
                content={
                    <Grid fluid>
                        <Row>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Profiles Contre Indiqués</ControlLabel>
                                    <Select
                                        options={profile.map(pro => ({
                                            label: pro.profile,
                                            value: pro.profile
                                        }))}
                                        isMulti
                                        placeholder={'Sélectionner des profils...'}
                                        onChange={(val) => setValue(val, 'profilesContreIndiquees')}
                                        value={state.profilesContreIndiquees}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Noms Commerciaux Contre Indiqués</ControlLabel>
                                    <AsyncSelect
                                        cacheOptions={true}
                                        defaultOptions={false}
                                        loadOptions={this.loadProductOptions}
                                        isMulti
                                        placeholder={loadingProducts ? 'Chargement...' : 'Rechercher un médicament (min. 2 caractères)...'}
                                        onChange={this.handleProductChange}
                                        value={nomCommercialValue}
                                        noOptionsMessage={({ inputValue }) => {
                                            if (!inputValue || inputValue.length === 0) {
                                                return 'Commencez à taper pour rechercher...';
                                            }
                                            if (inputValue.length < 2) {
                                                return 'Tapez au moins 2 caractères...';
                                            }
                                            if (loadError) {
                                                return loadError;
                                            }
                                            return 'Aucun médicament trouvé';
                                        }}
                                        loadingMessage={() => 'Chargement des médicaments...'}
                                        isSearchable={true}
                                        isClearable={true}
                                        isLoading={loadingProducts}
                                        formatOptionLabel={(option, { context }) => (
                                            <div>
                                                <strong>{option.label}</strong>
                                                {(option.forme || option.dosage) && context === 'menu' && (
                                                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                                                        {option.forme && `Forme: ${option.forme}`}
                                                        {option.dosage && ` - Dosage: ${option.dosage}`}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '42px'
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                zIndex: 9999
                                            })
                                        }}
                                    />
                                    {loadError && (
                                        <div className="text-danger" style={{ marginTop: '5px', fontSize: '12px' }}>
                                            {loadError}
                                        </div>
                                    )}
                                    <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                                        {nomCommercialValue.length > 0
                                            ? `${nomCommercialValue.length} médicament(s) sélectionné(s)`
                                            : 'Aucun médicament sélectionné'}
                                    </div>
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Statut Médicament</ControlLabel>
                                    <Select
                                        options={this.option.map(stat => ({
                                            label: stat.statut,
                                            value: stat.statut
                                        }))}
                                        placeholder={'Sélectionner un statut...'}
                                        onChange={(val) => setValue(val, 'statutMedicament')}
                                        value={state.statutMedicament}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Nom commercial *</ControlLabel>
                                    <FormControl
                                        placeholder="Nom commercial"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'nom')}
                                        value={state.nom || ''}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Forme *</ControlLabel>
                                    <FormControl
                                        placeholder="Forme"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'forme')}
                                        value={state.forme || ''}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Dosage *</ControlLabel>
                                    <FormControl
                                        placeholder="Dosage"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'dosage')}
                                        value={state.dosage || ''}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} md={12}>
                                <FormGroup>
                                    <ControlLabel>DCI *</ControlLabel>
                                    <ProductSubstanceTags
                                        value={state.dciTags || []}
                                        onChange={(val) => setValue(val, 'dciTags')}
                                        placeholder="Ajouter des DCI..."
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Conditionnement</ControlLabel>
                                    <FormControl
                                        placeholder="Conditionnement"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'conditionnement')}
                                        value={state.conditionnement || ''}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Famille thérapeutique *</ControlLabel>
                                    <FormControl
                                        placeholder="Famille thérapeutique"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'familleTherapeutique')}
                                        value={state.familleTherapeutique || ''}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Classes pharmacologiques *</ControlLabel>
                                    <FormControl
                                        placeholder="Classes pharmacologiques"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'classesPharmacologiques')}
                                        value={state.classesPharmacologiques || ''}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Fabricant *</ControlLabel>
                                    <FormControl
                                        placeholder="Fabricant"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'fabricant')}
                                        value={state.fabricant || ''}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Code ATC</ControlLabel>
                                    <FormControl
                                        placeholder="Code ATC"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'codeATC')}
                                        value={state.codeATC || ''}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Liste</ControlLabel>
                                    <FormControl
                                        placeholder="Liste"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'liste')}
                                        value={state.liste || ''}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Maladie traitée</ControlLabel>
                                    <FormControl
                                        placeholder="Maladie traitée"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'maladieTraitee')}
                                        value={state.maladieTraitee || ''}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xs={12} sm={6} md={4}>
                                <FormGroup>
                                    <ControlLabel>Références</ControlLabel>
                                    <FormControl
                                        placeholder="Références"
                                        type="text"
                                        onChange={(e) => setValue(e.target.value, 'references')}
                                        value={state.references || ''}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={12}>
                                <FormGroup>
                                    <ControlLabel>Ce produit est-il un produit référence?</ControlLabel>
                                    <br/>
                                    <Switch
                                        onText="OUI"
                                        offText="NON"
                                        value={state.isRef === 1}
                                        onChange={(elm, checked) => setValue(checked ? 1 : 0, 'isRef')}
                                    />
                                </FormGroup>
                                {state.isRef === 0 && (
                                    <FormGroup>
                                        <ControlLabel>ID du produit référence *</ControlLabel>
                                        <FormControl
                                            placeholder="ID du produit référence"
                                            type="text"
                                            onChange={(e) => setValue(e.target.value, 'referenceProductId')}
                                            value={state.referenceProductId || ''}
                                            required={state.isRef === 0}
                                        />
                                    </FormGroup>
                                )}
                            </Col>
                        </Row>
                    </Grid>
                }
            />
        );
    }
}

ProductBaseForm.propTypes = {
    state: PropTypes.object.isRequired,
    setValue: PropTypes.func.isRequired,
    profiles: PropTypes.array
};

ProductBaseForm.defaultProps = {
    profiles: []
};

export default ProductBaseForm;